/**
 * KPI Reorder Hook
 *
 * Persists KPI sort order to the database via API so all users see the same order.
 * The scorecard backend already sorts by sort_order, so after reorder + refetch,
 * all users get the updated order.
 */

import { useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { kpiKeys } from './useKpis';

/**
 * Reorder KPI definitions via API.
 * Accepts a flat array of ALL KPI IDs in the desired order.
 * The backend sets sort_order = array index for each KPI.
 */
async function reorderKpis(kpiIds: string[]): Promise<void> {
  await api.put('/v4/kpis/definitions/reorder', { kpiIds });
}

export function useKpiOrder() {
  const queryClient = useQueryClient();

  const reorderMutation = useMutation({
    mutationFn: reorderKpis,
    onSuccess: () => {
      // Refetch scorecard so all users see the new order
      queryClient.invalidateQueries({ queryKey: kpiKeys.scorecardAll() });
    },
    onError: (error) => {
      toast.error('Failed to reorder KPIs', {
        description: error instanceof Error ? error.message : 'Unknown error',
      });
      // Refetch to revert optimistic reorder
      queryClient.invalidateQueries({ queryKey: kpiKeys.scorecardAll() });
    },
  });

  const reorder = useCallback(
    (_category: string, kpiIds: string[]) => {
      reorderMutation.mutate(kpiIds);
    },
    [reorderMutation]
  );

  return { reorder };
}
